/**
 * rjsfShadcn — the react-jsonschema-form theme that maps a tailored
 * `$schema` document onto this app's vendored shadcn primitives.
 *
 * The platform serves ONE document per (resource × profession × unit)
 * (`GET /patients/$schema`), and every surface reads it: the server validates
 * against it, and the form renders from it. That only pays off if nothing on
 * this side re-declares the shape — so there is no field list here, no key
 * name, and no type switch beyond what JSON Schema itself carries. What this
 * file contributes is the two things a generic renderer cannot know:
 *
 *  1. **How a control looks here** — `@/components/ui/input`, the 44px touch
 *     target the ward forms use, the same select styling the census editor
 *     had. rjsf's stock theme is Bootstrap markup; these templates replace it.
 *  2. **What "empty" means here** — a nurse who tabs through an optional field
 *     without typing must send NO key, not `""`. The platform's floor rejects
 *     an empty string like any other wrong value, and a PUT is a complete view
 *     in which an omitted key CLEARS the field. So `BaseInputTemplate` emits
 *     `undefined` for an empty control, and `omitEmpty` below is the belt at
 *     the submit boundary: one function, so the two write paths (POST register,
 *     PUT edit) cannot drift on what they leave out.
 *
 * Labels come from the document, not from here: `x-tricorder.label` is an i18n
 * key when a layer declares one, and `patients.attributes.<key>` is the
 * platform convention when it does not. `fieldLabelKey` is the single place
 * that precedence is stated.
 */

import type { ChangeEvent, FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  BaseInputTemplateProps,
  DescriptionFieldProps,
  FieldTemplateProps,
  ObjectFieldTemplateProps,
  RJSFSchema,
  TitleFieldProps,
  WidgetProps,
} from '@rjsf/utils';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { humaniseAttributeKey } from '@/lib/patientLabel';

/** The 44px control height every ward form uses (Tailwind `11` = 2.75rem). */
const CONTROL_HEIGHT = 'min-h-11';
const SELECT_CLASSNAME =
  'flex min-h-11 w-full rounded-[8px] border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm';
const LABEL_CLASSNAME = 'block text-[15px] font-medium text-neutral-900';
const INVALID_CLASSNAME = 'border-destructive focus-visible:ring-destructive';

/** The platform's per-field annotation, as it rides on a JSON Schema property
 *  (`tricorder-go/schema/jsonschema.go` hangs the whole `Field` off it). Only
 *  the members this theme reads are named; the document carries more. */
interface FieldAnnotation {
  key?: string;
  label?: string;
  required?: boolean;
  classes?: string[] | null;
  strategy?: string;
}

/** Read the platform annotation off a schema property, if it carries one. */
export function annotationOf(schema: RJSFSchema | undefined): FieldAnnotation {
  const raw = (schema as Record<string, unknown> | undefined)?.['x-tricorder'];
  return typeof raw === 'object' && raw !== null ? (raw as FieldAnnotation) : {};
}

/**
 * The i18n key for a field's label: the layer's own `label` when it declares
 * one, else the platform convention `patients.attributes.<key>`. The humanised
 * key is the last resort — a layer can introduce a field faster than a
 * translation lands, and an untranslated field must still be legible.
 */
export function fieldLabelKey(key: string, annotation: FieldAnnotation): string {
  return annotation.label?.trim() ? annotation.label : `patients.attributes.${key}`;
}

/**
 * Drop every key whose value is absent or an empty string.
 *
 * Absence is the vocabulary: there is no `null` and no `""` for "not recorded".
 * On a POST an omitted key is simply not recorded; on a PUT — which replaces
 * the caller's complete writable view — it CLEARS the field. Both write paths
 * run through here so they cannot disagree about which is which.
 */
export function omitEmpty(data: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data ?? {})) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    out[key] = value;
  }
  return out;
}

/** The last path segment of an rjsf field id — the schema key it edits. */
function keyOfId(id: string): string {
  const parts = id.split('_');
  return parts[parts.length - 1] ?? id;
}

/**
 * One field: label, required marker, control, errors.
 *
 * The required marker is read off `required`, which rjsf derives from the
 * document's own `required` array — so a ward that starts demanding `glasgow`
 * changes what this renders with no frontend release.
 */
function TricorderFieldTemplate(props: FieldTemplateProps) {
  const { t } = useTranslation();
  const { id, classNames, style, children, required, rawErrors, schema, hidden } = props;
  if (hidden) return <div className="hidden">{children}</div>;

  const annotation = annotationOf(schema);
  const key = annotation.key ?? keyOfId(id);
  const errors = rawErrors ?? [];

  return (
    <div className={cn('space-y-1.5', classNames)} style={style}>
      <label htmlFor={id} className={LABEL_CLASSNAME}>
        {t(fieldLabelKey(key, annotation), humaniseAttributeKey(key))}
        {required && (
          <span
            className="ml-1 text-[13px] font-normal text-neutral-500"
            data-testid={`required-marker-${key}`}
          >
            {t('common.required')}
          </span>
        )}
      </label>
      {children}
      {schema.pattern && (
        <p className="text-xs text-muted-foreground">
          {t('patients.attributes.pattern_hint', { pattern: schema.pattern })}
        </p>
      )}
      {errors.map((error) => (
        <p key={error} className="text-[13px] text-destructive" data-testid={`field-error-${key}`}>
          {error}
        </p>
      ))}
    </div>
  );
}

/** The attribute group: fields stacked, no card, no legend — the surrounding
 *  form supplies its own headings and disclosure. */
function TricorderObjectFieldTemplate({ properties }: ObjectFieldTemplateProps) {
  return (
    <div className="space-y-4">
      {properties.map((property) => (
        <div key={property.name}>{property.content}</div>
      ))}
    </div>
  );
}

/** rjsf renders a title and description per object by default. The schema's
 *  `$defs.create` has neither worth showing, and both push the first control
 *  off a phone screen. */
const NoTitle = (_props: TitleFieldProps) => null;
const NoDescription = (_props: DescriptionFieldProps) => null;

/**
 * Every free-text and numeric control.
 *
 * The empty value is `undefined`, not `''`: see the module comment. Numbers are
 * parsed here rather than coerced at the submit boundary, because rjsf already
 * knows the declared type and a second type switch downstream would be a
 * second place for it to be wrong. An UNPARSEABLE number is passed through as
 * typed, so the server's own violation names the field instead of the form
 * silently discarding what the nurse wrote.
 */
function TricorderBaseInputTemplate(props: BaseInputTemplateProps) {
  const { id, value, required, readonly, disabled, autofocus, onChange, onBlur, onFocus, options, schema, rawErrors, type, placeholder } =
    props;
  const numeric = schema.type === 'integer' || schema.type === 'number';
  const invalid = (rawErrors ?? []).length > 0;

  const emit = (raw: string) => {
    if (raw === '') {
      onChange(options?.emptyValue ?? undefined);
      return;
    }
    if (!numeric) {
      onChange(raw);
      return;
    }
    const parsed = Number(raw);
    onChange(Number.isFinite(parsed) ? parsed : raw);
  };

  return (
    <Input
      id={id}
      name={id}
      type={numeric ? 'number' : (type ?? 'text')}
      value={value === undefined || value === null ? '' : String(value)}
      placeholder={placeholder}
      required={required}
      readOnly={readonly}
      disabled={disabled}
      autoFocus={autofocus}
      autoComplete="off"
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      // Native constraint hint only — the server's anchored RE2 check is the
      // authority; this just lets the browser flag the shape early.
      pattern={typeof schema.pattern === 'string' ? schema.pattern : undefined}
      className={cn(CONTROL_HEIGHT, invalid && INVALID_CLASSNAME)}
      onChange={(e: ChangeEvent<HTMLInputElement>) => emit(e.target.value)}
      onBlur={(e: FocusEvent<HTMLInputElement>) => onBlur?.(id, e.target.value)}
      onFocus={(e: FocusEvent<HTMLInputElement>) => onFocus?.(id, e.target.value)}
    />
  );
}

/**
 * A closed set — a CUE disjunction, exported as `enum`.
 *
 * A ward that narrows `mobility` to two of its three values changes what this
 * renders, on the next request, with nothing shipped. The blank option is how
 * an optional closed-set field goes back to "not recorded".
 */
function TricorderSelectWidget(props: WidgetProps) {
  const { id, value, required, disabled, readonly, autofocus, onChange, onBlur, onFocus, options, rawErrors } =
    props;
  const enumOptions = options?.enumOptions ?? [];
  const invalid = (rawErrors ?? []).length > 0;

  return (
    <select
      id={id}
      name={id}
      value={value === undefined || value === null ? '' : String(value)}
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      className={cn(SELECT_CLASSNAME, invalid && INVALID_CLASSNAME)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === '') {
          onChange(undefined);
          return;
        }
        // Give back the option's own value, so an integer set stays integers.
        const chosen = enumOptions.find((o) => String(o.value) === raw);
        onChange(chosen ? chosen.value : raw);
      }}
      onBlur={(e) => onBlur?.(id, e.target.value)}
      onFocus={(e) => onFocus?.(id, e.target.value)}
    >
      <option value="" />
      {enumOptions.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/** The templates this app hands `<Form>`. */
export const shadcnTemplates = {
  FieldTemplate: TricorderFieldTemplate,
  ObjectFieldTemplate: TricorderObjectFieldTemplate,
  BaseInputTemplate: TricorderBaseInputTemplate,
  TitleFieldTemplate: NoTitle,
  DescriptionFieldTemplate: NoDescription,
};

/** The widgets this app hands `<Form>`. */
export const shadcnWidgets = {
  SelectWidget: TricorderSelectWidget,
};
